/**
 * Image validation and processing utilities
 */

export interface ImageValidationResult {
  valid: boolean;
  error?: string;
  image?: HTMLImageElement;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

// Configuration
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_GIF_SIZE = 10 * 1024 * 1024; // 10MB for GIFs
const MIN_DIMENSIONS = { width: 200, height: 200 };
const RECOMMENDED_DIMENSIONS = { width: 500, height: 500 };
const ACCEPTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validates image file for proper dimensions, size and format
 */
export const validateImageFile = (file: File): Promise<ImageValidationResult> => {
  return new Promise((resolve) => {
    // Check file size - use larger limit for GIFs
    const maxSize = file.type === 'image/gif' ? MAX_GIF_SIZE : MAX_FILE_SIZE;
    if (file.size > maxSize) {
      resolve({
        valid: false,
        error: `File is too large. Maximum size is ${maxSize / (1024 * 1024)}MB${file.type === 'image/gif' ? ' for GIF files' : ''}.`
      });
      return;
    }

    // Check file type
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      resolve({
        valid: false,
        error: `Invalid file format. Accepted formats: JPEG, PNG, GIF, WEBP.`
      });
      return;
    }

    // Check image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      if (img.width < MIN_DIMENSIONS.width || img.height < MIN_DIMENSIONS.height) {
        resolve({
          valid: false,
          error: `Image is too small. Minimum dimensions: ${MIN_DIMENSIONS.width}x${MIN_DIMENSIONS.height}px.`
        });
        return;
      }

      resolve({
        valid: true,
        image: img
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        valid: false,
        error: 'Failed to load image. The file may be corrupted.'
      });
    };

    img.src = objectUrl;
  });
};

/**
 * Processes an image file without cropping (preserves original image shape)
 * For GIFs, returns the original file to preserve animation
 * @param file Original image file
 * @param quality JPEG quality (0-1) - only used for non-GIF images
 */
export const createCircularAvatar = async (
  file: File,
  cropScale: number = 1.0,
  quality: number = 0.9
): Promise<Blob | null> => {
  try {
    // For GIFs, return the original file to preserve animation
    if (file.type === 'image/gif') {
      console.log('Processing GIF avatar - preserving original file');
      return file;
    }

    // Validate file first
    const validation = await validateImageFile(file);
    if (!validation.valid || !validation.image) {
      console.error('Image validation failed:', validation.error);
      return null;
    }

    const img = validation.image;

    // Create canvas for image processing
    const canvas = document.createElement('canvas');
    // Keep canvas size as original dimensions for output quality
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return null;
    }

    // Apply zoom/crop if scale is greater than 1
    if (cropScale > 1) {
      // Calculate the dimensions of the source crop area
      const cropWidth = img.width / cropScale;
      const cropHeight = img.height / cropScale;

      // Center the crop
      const cropX = (img.width - cropWidth) / 2;
      const cropY = (img.height - cropHeight) / 2;

      // Draw the zoomed/cropped portion
      // ctx.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
      ctx.drawImage(
        img,
        cropX, cropY, cropWidth, cropHeight,  // Source crop area (center portion)
        0, 0, img.width, img.height            // Destination (fill entire canvas)
      );

      console.log(`Applied zoom scale ${cropScale}: cropped ${cropWidth}x${cropHeight} from center`);
    } else {
      // No zoom, draw original image
      ctx.drawImage(img, 0, 0, img.width, img.height);
    }

    // Convert to blob with compression
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob),
        'image/jpeg',
        quality
      );
    });
  } catch (error) {
    console.error('Error processing avatar image:', error);
    return null;
  }
};

/**
 * Creates a preview data URL for an image file
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Checks if an image is likely too pixelated based on dimensions
 */
export const isLikelyPixelated = (image: HTMLImageElement): boolean => {
  return image.width < RECOMMENDED_DIMENSIONS.width || 
         image.height < RECOMMENDED_DIMENSIONS.height;
};

/**
 * Provides user-friendly feedback based on image analysis
 */
export const getImageQualityFeedback = (image: HTMLImageElement): string | null => {
  if (isLikelyPixelated(image)) {
    return "This image may appear pixelated. For best results, use images at least 500x500 pixels.";
  }
  
  const aspectRatio = image.width / image.height;
  if (aspectRatio < 0.75 || aspectRatio > 1.33) {
    return "This image has an unusual aspect ratio. Square images work best for profile pictures.";
  }
  
  return null;
};

/**
 * Ensures an image URL is suitable for social media sharing
 * Filters out data URLs and blob URLs which won't work on social media
 * @param url The image URL to validate
 * @returns The URL if valid for social media, null otherwise
 */
export const getSocialMediaSafeImageUrl = (url: string | null | undefined): string | null => {
  if (!url) {
    console.log('[getSocialMediaSafeImageUrl] No URL provided');
    return null;
  }
  
  // Data URLs and blob URLs won't work for social media sharing
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    console.log('[getSocialMediaSafeImageUrl] Rejected data/blob URL:', url.substring(0, 50) + '...');
    return null;
  }
  
  // Handle Supabase URLs - ensure they're properly formatted
  if (url.includes('supabase.co')) {
    console.log('[getSocialMediaSafeImageUrl] Processing Supabase URL:', url);
    
    // Ensure Supabase URLs are HTTPS
    if (url.startsWith('http://')) {
      url = url.replace('http://', 'https://');
      console.log('[getSocialMediaSafeImageUrl] Converted to HTTPS:', url);
    }
    
    // Check if it's a public Supabase storage URL
    if (url.includes('/storage/v1/object/public/')) {
      console.log('[getSocialMediaSafeImageUrl] Valid public Supabase URL');
      return url;
    }
    
    // If it's a relative Supabase URL, it should be fine
    if (url.startsWith('/')) {
      console.log('[getSocialMediaSafeImageUrl] Relative Supabase URL, will be made absolute');
      return url;
    }
    
    // If it's not a public storage URL, it might be private
    console.warn('[getSocialMediaSafeImageUrl] Supabase URL might be private:', url);
    return null;
  }
  
  // Handle other external URLs
  if (url.startsWith('http')) {
    console.log('[getSocialMediaSafeImageUrl] External URL:', url);
    return url;
  }
  
  // Handle relative URLs
  if (url.startsWith('/')) {
    console.log('[getSocialMediaSafeImageUrl] Relative URL:', url);
    return url;
  }
  
  // If it's not a valid URL format, reject it
  try {
    new URL(url);
    console.log('[getSocialMediaSafeImageUrl] Valid URL:', url);
    return url;
  } catch (e) {
    console.warn('[getSocialMediaSafeImageUrl] Invalid URL format:', url);
    return null;
  }
};
